/**
 * OAuth API Client
 * Framework-agnostic HTTP client for OAuth service communication
 */

import type {
  Platform,
  OAuthConnection,
  OAuthConnectionStatus,
  InitiateOAuthResponse,
  OAuthCallbackResult,
  EnhancedOAuthConnection,
  UpdateConnectionLabelRequest,
  VerifyConnectionResponse,
  ConnectionsByOrgResponse,
} from './types';
import { getAuthToken } from '../../services/api';

export class OAuthApiClient {
  private baseUrl: string;
  private getAuthToken: () => string | null;

  /**
   * Initialize OAuth API client
   *
   * @param baseUrl - Base URL of the backend API (default: from env)
   * @param tokenGetter - Function to retrieve current auth token (default: checks both localStorage and sessionStorage)
   */
  constructor(
    baseUrl: string = import.meta.env.VITE_API_BASE_URL || `${window.location.protocol}//${window.location.hostname}:8080`,
    tokenGetter: () => string | null = getAuthToken
  ) {
    this.baseUrl = baseUrl;
    this.getAuthToken = tokenGetter;
  }

  /**
   * Get authorization headers with Bearer token
   */
  private getHeaders(): HeadersInit {
    const token = this.getAuthToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Initiate OAuth flow for a platform
   *
   * @param platform - Platform to connect ('meta', 'google_ads', 'tiktok')
   * @param redirectUri - OAuth callback URI
   * @returns Authorization URL to redirect user to
   */
  async initiate(platform: Platform, redirectUri: string): Promise<InitiateOAuthResponse> {
    const response = await fetch(`${this.baseUrl}/api/v1/oauth/initiate`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ platform, redirect_uri: redirectUri }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'OAuth initiation failed' }));
      throw new Error(error.detail || 'Failed to initiate OAuth');
    }

    return response.json();
  }

  /**
   * Complete OAuth flow with authorization code
   *
   * @param code - Authorization code from platform
   * @param state - State parameter for CSRF verification
   * @returns Connection result
   */
  async callback(code: string, state: string): Promise<OAuthCallbackResult> {
    const response = await fetch(`${this.baseUrl}/api/v1/oauth/callback`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ code, state }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'OAuth callback failed' }));
      throw new Error(error.detail || 'Failed to complete OAuth');
    }

    return response.json();
  }

  /**
   * Get all OAuth connections for current user
   *
   * @param platform - Optional filter by platform
   * @returns List of connections
   */
  async getConnections(platform?: Platform): Promise<OAuthConnection[]> {
    const url = new URL(`${this.baseUrl}/api/v1/oauth/connections`);
    if (platform) {
      url.searchParams.set('platform', platform);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch connections' }));
      throw new Error(error.detail || 'Failed to get connections');
    }

    return response.json();
  }

  /**
   * Check connection status for a platform
   *
   * @param platform - Platform to check
   * @returns Connection status
   */
  async getStatus(platform: Platform): Promise<OAuthConnectionStatus> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/oauth/connections/status?platform=${platform}`,
      {
        method: 'GET',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      return {
        connected: false,
        platform,
        error: 'Failed to check connection status',
      };
    }

    return response.json();
  }

  /**
   * Disconnect/revoke an OAuth connection
   *
   * @param connectionId - Connection ID to revoke
   */
  async disconnect(connectionId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/v1/oauth/connections/${connectionId}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to disconnect' }));
      throw new Error(error.detail || 'Failed to disconnect connection');
    }
  }

  /**
   * Get list of supported platforms
   *
   * @returns Array of available platform identifiers based on configured credentials
   */
  async getPlatforms(): Promise<Platform[]> {
    const response = await fetch(`${this.baseUrl}/api/v1/oauth/platforms`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      // Fallback: return empty array instead of assuming platforms are available
      return [];
    }

    const data = await response.json();
    if (!data.platforms || !Array.isArray(data.platforms)) {
      return [];
    }

    // Extract platform IDs from the response objects
    return data.platforms
      .map((p: any) => p.id || p)
      .filter((id: string): id is Platform => {
        const validPlatforms: Platform[] = ['meta', 'google_ads', 'tiktok', 'twitter', 'pinterest'];
        return validPlatforms.includes(id as Platform);
      });
  }

  /**
   * Get enhanced connections with health status (Phase 4)
   *
   * @param platform - Optional filter by platform
   * @param includeRevoked - Include revoked connections
   * @returns List of enhanced connections
   */
  async getConnectionsEnhanced(
    platform?: Platform,
    includeRevoked = false
  ): Promise<EnhancedOAuthConnection[]> {
    const url = new URL(`${this.baseUrl}/api/v1/oauth/connections/enhanced`);
    if (platform) {
      url.searchParams.set('platform', platform);
    }
    url.searchParams.set('include_revoked', includeRevoked.toString());

    console.log('[OAuthApiClient] getConnectionsEnhanced - Request:', {
      url: url.toString(),
      platform,
      includeRevoked,
      hasToken: !!this.getAuthToken(),
    });

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: this.getHeaders(),
      });

      console.log('[OAuthApiClient] getConnectionsEnhanced - Response status:', response.status);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Failed to fetch enhanced connections' }));
        console.error('[OAuthApiClient] getConnectionsEnhanced - Error response:', {
          status: response.status,
          error,
        });
        throw new Error(error.detail || 'Failed to get enhanced connections');
      }

      const data = await response.json();
      console.log('[OAuthApiClient] getConnectionsEnhanced - Success:', {
        count: Array.isArray(data) ? data.length : 'not array',
        data,
      });

      return data;
    } catch (error) {
      console.error('[OAuthApiClient] getConnectionsEnhanced - Exception:', error);
      throw error;
    }
  }

  /**
   * Set a connection as default for its organization/platform
   *
   * @param connectionId - Connection ID to set as default
   */
  async setDefaultConnection(connectionId: string): Promise<EnhancedOAuthConnection> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/oauth/connections/${connectionId}/set-default`,
      {
        method: 'PATCH',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to set default' }));
      throw new Error(error.detail || 'Failed to set default connection');
    }

    return response.json();
  }

  /**
   * Update connection label
   *
   * @param connectionId - Connection ID to update
   * @param label - New label for the connection
   */
  async updateConnectionLabel(
    connectionId: string,
    label: string
  ): Promise<EnhancedOAuthConnection> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/oauth/connections/${connectionId}/label`,
      {
        method: 'PATCH',
        headers: this.getHeaders(),
        body: JSON.stringify({ label }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to update label' }));
      throw new Error(error.detail || 'Failed to update connection label');
    }

    return response.json();
  }

  /**
   * Verify connection health
   *
   * @param connectionId - Connection ID to verify
   * @returns Verification result with updated connection status
   */
  async verifyConnection(connectionId: string): Promise<VerifyConnectionResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/v1/oauth/connections/${connectionId}/verify`,
      {
        method: 'POST',
        headers: this.getHeaders(),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to verify connection' }));
      throw new Error(error.detail || 'Failed to verify connection');
    }

    return response.json();
  }

  /**
   * Get connections for all organizations the user belongs to
   *
   * This endpoint is optimized for UI components that need to display connection
   * status for multiple organizations at once (e.g., org switcher dropdown).
   * Instead of making one API call per organization, this returns all connections
   * grouped by organization_id in a single efficient call.
   *
   * @param platform - Optional filter by platform
   * @param includeRevoked - Include revoked connections
   * @returns Connections grouped by organization ID
   */
  async getConnectionsForAllOrganizations(
    platform?: Platform,
    includeRevoked = false
  ): Promise<ConnectionsByOrgResponse> {
    const url = new URL(`${this.baseUrl}/api/v1/oauth/connections/all-organizations`);
    if (platform) {
      url.searchParams.set('platform', platform);
    }
    url.searchParams.set('include_revoked', includeRevoked.toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to fetch all org connections' }));
      throw new Error(error.detail || 'Failed to get connections for all organizations');
    }

    return response.json();
  }
}
